import { useMemo } from 'react'

import {
  type ActivityLog,
  type ActivityLogChangeRow,
  formatChangeValue,
  humanizeToken,
  toChangeRows,
} from './types'

export interface ActivityLogDetailProps {
  log: ActivityLog
}

/**
 * Placeholders `formatChangeValue` emits when there is no value to show. They are prose,
 * not data, so they are rendered as prose — never in the monospace value voice.
 */
const PLACEHOLDER_LABELS: Record<string, string> = {
  '—': 'not set',
  '(empty)': 'empty',
}

/** What a purge leaves behind in a masked column — muted, not mistaken for real data. */
const PURGE_MASK = '**'

/**
 * Shared micro-label voice for the column headers and section captions. `text-left` is
 * explicit because the UA centres `th` and Preflight does not reset it.
 */
const CAPTION = 'text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400'

/**
 * `humanizeToken` flattens `.` and `_` alike, so `settings.theme` and `settings_theme`
 * both read "Settings theme". Only show the raw token when that flattening lost something.
 */
function losesInformation(field: string, label: string): boolean {
  return label.toLowerCase().replace(/ /g, '_') !== field
}

/**
 * The two non-values that can land in any scalar slot — an absent value and a purge mask.
 * Both are muted so they never read as recorded data, wherever they surface.
 */
function nonValue(value: string): { text: string; className: string; title?: string } | null {
  const placeholder = PLACEHOLDER_LABELS[value]
  if (placeholder) return { text: placeholder, className: 'italic text-gray-400' }

  if (value === PURGE_MASK) {
    return { text: PURGE_MASK, className: 'font-mono text-gray-400', title: 'Value purged' }
  }

  return null
}

function ChangeValue({ value, emphasis = false }: { value: string; emphasis?: boolean }) {
  const muted = nonValue(value)

  if (muted) {
    return (
      <span title={muted.title} className={`text-xs ${muted.className}`}>
        {muted.text}
      </span>
    )
  }

  return (
    <span
      title={value}
      className={`line-clamp-3 break-all font-mono text-xs ${
        emphasis ? 'font-medium text-gray-900' : 'text-gray-500'
      }`}
    >
      {value}
    </span>
  )
}

/**
 * The sanitized diff. Field names size to their content and the two value columns split
 * whatever is left, so the panel reads the same whether it is 400px or 1600px wide.
 */
function ChangesCard({ rows }: { rows: ActivityLogChangeRow[] }) {
  return (
    <section className="min-w-0 overflow-hidden rounded-md border border-gray-200 bg-white @5xl:flex-1">
      {/* Auto layout: `w-[1%]` shrink-wraps the field column, the 50/50 hints split the rest. */}
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-gray-100">
            <th scope="col" className={`${CAPTION} w-[1%] whitespace-nowrap px-3 py-1.5`}>
              Changes
            </th>
            <th scope="col" className={`${CAPTION} w-1/2 py-1.5`}>
              From
            </th>
            <th aria-hidden className="p-0" />
            <th scope="col" className={`${CAPTION} w-1/2 py-1.5 pr-3`}>
              To
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.key}>
              <th scope="row" className="w-[1%] px-3 py-1.5 text-left align-baseline font-normal">
                {/* Caps how far a pathological field name can squeeze the value columns. */}
                <div className="max-w-[240px]">
                  <span className="block truncate text-[13px] text-gray-900" title={row.field}>
                    {row.label}
                  </span>
                  {losesInformation(row.field, row.label) && (
                    <span className="block truncate font-mono text-[11px] text-gray-400">
                      {row.field}
                    </span>
                  )}
                </div>
              </th>

              {row.redacted ? (
                <td colSpan={3} className="py-1.5 pr-3 align-baseline">
                  <span className="inline-flex items-center rounded-sm bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                    Redacted
                  </span>
                  <span className="ml-2 text-[11px] text-gray-400">value not stored</span>
                </td>
              ) : (
                <>
                  <td className="py-1.5 align-baseline">
                    <ChangeValue value={row.from} />
                  </td>
                  {/* Padding, not width — auto layout collapses a width hint to the glyph. */}
                  <td
                    aria-hidden
                    className="px-2.5 py-1.5 text-center align-baseline text-xs text-gray-300"
                  >
                    &rarr;
                  </td>
                  <td className="py-1.5 pr-3 align-baseline">
                    <ChangeValue value={row.to} emphasis />
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

interface ContextItem {
  key: string
  label: string
  value: string
  /** Job ids are identifiers — monospace and tinted so they read as a different kind of thing. */
  accent?: boolean
}

/**
 * Sanitized metadata plus the job id, as a single wrapping cloud of `label value` chips.
 * A cloud packs five short scalars onto one line at table width and degrades gracefully
 * in the narrow rail beside a diff — no orphaned grid cells either way.
 */
function ContextCard({
  items,
  note,
  className = '',
}: {
  items: ContextItem[]
  /** Right-aligned caption — carries the "no diff" empty state at zero extra height. */
  note?: string
  className?: string
}) {
  return (
    <section
      className={`min-w-0 overflow-hidden rounded-md border border-gray-200 bg-white ${className}`}
    >
      <header className="flex items-baseline justify-between gap-3 border-b border-gray-100 px-3 py-1.5">
        <h4 className={`${CAPTION} m-0`}>Context</h4>
        {note && <span className="truncate text-[11px] text-gray-400">{note}</span>}
      </header>

      <dl className="m-0 flex flex-wrap gap-1.5 p-2">
        {items.map((item) => {
          const muted = nonValue(item.value)
          const valueTone = muted
            ? muted.className
            : `font-medium text-gray-900 ${item.accent ? 'font-mono' : ''}`

          return (
            <div
              key={item.key}
              className={`inline-flex max-w-full items-baseline gap-1.5 rounded border px-2 py-1 ${
                item.accent ? 'border-[#DAEDFF] bg-[#DAEDFF]/50' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <dt
                className={`shrink-0 text-[11px] ${
                  item.accent ? 'text-[#2B65B0]' : 'text-gray-500'
                }`}
              >
                {item.label}
              </dt>
              <dd
                title={muted?.title ?? item.value}
                className={`m-0 min-w-0 truncate text-xs ${valueTone}`}
              >
                {muted ? muted.text : item.value}
              </dd>
            </div>
          )
        })}
      </dl>
    </section>
  )
}

/**
 * Expanded-row content for a single activity log entry: the sanitized diff, the sanitized
 * metadata, and — for rows recorded from a background job — the job id that ties the entry
 * back to that job.
 *
 * Every section is conditional. A large share of actions (`*.reorder`, bulk summaries,
 * deletes, denials, exports) carry no field diff at all, so the missing diff costs a caption
 * rather than an illustration, and the panel collapses to a single line when there is nothing
 * at all to show.
 */
export function ActivityLogDetail({ log }: ActivityLogDetailProps) {
  const changeRows = useMemo(() => toChangeRows(log.recorded_changes), [log.recorded_changes])

  const contextItems = useMemo<ContextItem[]>(() => {
    const items: ContextItem[] = Object.entries(log.metadata || {}).map(([key, value]) => ({
      key,
      label: humanizeToken(key),
      value: formatChangeValue(value),
    }))

    if (log.job_id) {
      items.push({ key: '__job_id', label: 'Job', value: log.job_id, accent: true })
    }

    return items
  }, [log.metadata, log.job_id])

  const hasChanges = changeRows.length > 0
  const hasContext = contextItems.length > 0

  return (
    // The accent rail ties the panel to the row above it when several rows are open at once.
    // `@container`, not a viewport breakpoint: the table scrolls horizontally, so this panel
    // stays ~1600px wide on a 1000px screen and the split must key off its own width.
    <div className="@container border-l-[3px] border-[#2B65B0] bg-[#F3F5F8] px-4 py-3">
      {!hasChanges && !hasContext ? (
        <p className="m-0 text-xs text-gray-400">
          No field changes or metadata recorded for this action.
        </p>
      ) : (
        <div className="flex flex-col gap-3 @5xl:flex-row @5xl:items-start">
          {hasChanges && <ChangesCard rows={changeRows} />}
          {hasContext && (
            <ContextCard
              items={contextItems}
              note={hasChanges ? undefined : 'No field changes recorded'}
              className={hasChanges ? '@5xl:w-[420px] @5xl:shrink-0' : 'w-full'}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default ActivityLogDetail
